import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import DashboardLayout from "../components/DashboardLayout";
import { useAuth } from "../lib/auth";
import usePrintingData from "../hooks/usePrintingData";
import { TabBar, TabPanel, Notice, Loading, btnSecondary, btnLink } from "../components/printing/PrintingUi";
import PrintingOverview from "../components/printing/PrintingOverview";
import PrintAgentsPanel from "../components/printing/PrintAgentsPanel";
import PrintersPanel from "../components/printing/PrintersPanel";
import DocumentDefaultsPanel from "../components/printing/DocumentDefaultsPanel";
import RulesPanel from "../components/printing/RulesPanel";
import LayoutsPanel from "../components/printing/LayoutsPanel";
import JobsPanel from "../components/printing/JobsPanel";

/**
 * /dashboard/printing — tenant-admin control centre for the reusable
 * printing platform (Phase 5). Every action goes through the Phase 3
 * backend API; the browser never talks to a printer. Mutating controls
 * are shown only to business administrators; everyone else gets a
 * read-only view.
 */
export const TABS = [
  { id: "overview", label: "Overview" },
  { id: "agents", label: "Print Agents" },
  { id: "printers", label: "Printers" },
  { id: "defaults", label: "Defaults & Catalog Overrides" },
  { id: "rules", label: "Rules & Triggers" },
  { id: "layouts", label: "Layouts" },
  { id: "jobs", label: "Print Jobs & Audit" },
];

export function isTenantAdmin(user) {
  return Boolean(user && user.role === "business_admin");
}

function tabFromHash() {
  if (typeof window === "undefined") return "overview";
  const h = (window.location.hash || "").replace("#", "");
  return TABS.some((t) => t.id === h) ? h : "overview";
}

export default function PrintingAdmin() {
  const { user } = useAuth();
  const isAdmin = isTenantAdmin(user);
  const data = usePrintingData();
  const [tab, setTab] = useState(tabFromHash);
  const [jobsFilter, setJobsFilter] = useState(null);

  const goTo = useCallback((id, filter) => {
    setTab(id);
    if (filter) setJobsFilter(filter);
    try { window.history.replaceState(null, "", `#${id}`); } catch (_e) { /* ignore */ }
  }, []);
  useEffect(() => {
    const onHash = () => setTab(tabFromHash());
    window.addEventListener("hashchange", onHash);
    return () => window.removeEventListener("hashchange", onHash);
  }, []);

  const notify = useCallback((kind, message) => {
    if (kind === "success") toast.success(message);
    else if (kind === "error") toast.error(message);
    else toast.message(message);
  }, []);

  const ambiguousCount = data.jobs.filter((j) => j.state === "ambiguous").length;
  const tabs = TABS.map((t) => (t.id === "jobs" ? { ...t, badge: ambiguousCount } : t));

  return (
    <DashboardLayout>
      <div className="mx-auto max-w-6xl space-y-4 p-4 sm:p-6" data-testid="printing-admin-page">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <h1 className="text-2xl font-serif text-stone-900">Printing</h1>
            <p className="text-sm text-stone-600">Receipts, kitchen tickets and packing slips on network printers driven by the Rudras Print Agent.</p>
          </div>
          <button type="button" className={btnSecondary} onClick={data.reload} data-testid="printing-refresh">Refresh</button>
        </div>
        {!isAdmin && <Notice tone="info" testId="read-only-notice">Read-only: only a business administrator can change printing settings, issue tokens, or reprint.</Notice>}
        {data.error && <Notice tone="danger" testId="printing-load-error">{data.error} <button type="button" className={btnLink} onClick={data.reload}>Try again</button></Notice>}
        <TabBar tabs={tabs} active={tab} onChange={(id) => goTo(id)} />
        {data.loading && !data.profiles ? <Loading label="Loading printing settings…" /> : (
          <>
            <TabPanel id="overview" active={tab}><PrintingOverview agents={data.agents} printers={data.printers} jobs={data.jobs} documents={data.documents} automaticPrinting={data.automaticPrinting} isAdmin={isAdmin} onChanged={data.reloadConfig} notify={notify} onGoTo={goTo} /></TabPanel>
            <TabPanel id="agents" active={tab}><PrintAgentsPanel agents={data.agents} printers={data.printers} isAdmin={isAdmin} onChanged={data.reloadLive} notify={notify} /></TabPanel>
            <TabPanel id="printers" active={tab}><PrintersPanel printers={data.printers} agents={data.agents} profiles={data.profiles} isAdmin={isAdmin} onChanged={data.reloadLive} notify={notify} onViewJobs={(printerId) => goTo("jobs", { printerId })} /></TabPanel>
            <TabPanel id="defaults" active={tab}><DocumentDefaultsPanel documents={data.documents} printers={data.printers} layouts={data.layouts} catalogs={data.catalogs} isAdmin={isAdmin} onChanged={data.reloadConfig} notify={notify} /></TabPanel>
            <TabPanel id="rules" active={tab}><RulesPanel rules={data.rules} catalogs={data.catalogs} printers={data.printers} layouts={data.layouts} isAdmin={isAdmin} onChanged={data.reloadConfig} notify={notify} onGoTo={goTo} /></TabPanel>
            <TabPanel id="layouts" active={tab}><LayoutsPanel layouts={data.layouts} sections={data.sections} isAdmin={isAdmin} onChanged={data.reloadConfig} notify={notify} /></TabPanel>
            <TabPanel id="jobs" active={tab}><JobsPanel jobs={data.jobs} printers={data.printers} agents={data.agents} isAdmin={isAdmin} initialFilter={jobsFilter} onChanged={data.reloadLive} notify={notify} /></TabPanel>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
